// Web/PWA notification helpers: badge counts, system-banner sync, dismissal.
//
// This module complements `lib/push.js` (which handles FCM token + foreground
// onMessage). Together they implement the OS-level notification spec:
//   ✅ System banner on every unread event           (handled in push.js)
//   ✅ App-icon badge with current unread count       (Badging API, here)
//   ✅ Permission check + request flow                (here + push.js)
//   ✅ Auto-dismiss in-tray banner when read in-app   (here)
//
// Browser support note:
//   - `navigator.setAppBadge()` is supported in Chrome/Edge desktop and Chrome
//     Android (when the PWA is installed to home screen). Safari/iOS only
//     show badges on the home-screen icon for installed PWAs.
//   - We treat all of these as best-effort: if the API is missing we just
//     don't badge — no errors, no console noise.

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Set the app-icon badge to `count`. Pass 0 to clear. No-op when the host
 * browser doesn't expose the Badging API (e.g. Firefox, Safari without
 * installed PWA). Wraps both the Document and Navigator targets so it works
 * inside the page AND in the service worker if used there.
 */
export function setBadge(count) {
  try {
    const n = Number(count) || 0;
    if (n <= 0) {
      if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
      else if (navigator.setAppBadge) navigator.setAppBadge(0).catch(() => {});
    } else if (navigator.setAppBadge) {
      navigator.setAppBadge(n).catch(() => {});
    }
  } catch { /* unsupported */ }
}

/** Force-clear the app badge — call on logout or when user opens /notifications. */
export function clearBadge() { setBadge(0); }

/**
 * Dismiss any system-tray banners whose tag matches the given prefix.
 * The service worker's `getNotifications()` returns active notifications;
 * we filter and call `.close()` on each one. Use this when the user reads
 * a notification in-app so the banner doesn't linger in the OS tray.
 */
export async function dismissSystemNotifications({ tag, refId } = {}) {
  if (!('serviceWorker' in navigator)) return 0;
  try {
    const reg = await navigator.serviceWorker.ready;
    if (!reg || !reg.getNotifications) return 0;
    const all = await reg.getNotifications();
    let closed = 0;
    for (const n of all) {
      if (tag && n.tag !== tag) continue;
      if (refId && n.data?.refId !== refId) continue;
      n.close();
      closed += 1;
    }
    return closed;
  } catch {
    return 0;
  }
}

/** Dismiss every Tirth ERP system notification (used when /notifications opens). */
export async function dismissAllSystemNotifications() {
  if (!('serviceWorker' in navigator)) return 0;
  try {
    const reg = await navigator.serviceWorker.ready;
    if (!reg || !reg.getNotifications) return 0;
    const all = await reg.getNotifications();
    all.forEach(n => n.close());
    return all.length;
  } catch {
    return 0;
  }
}

/**
 * Pull the current unread count from the backend and set the app badge.
 * Returns the count. Safe to call on a poll loop.
 */
export async function syncBadgeFromServer() {
  try {
    const r = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
    const count = r.data?.count ?? 0;
    setBadge(count);
    return count;
  } catch {
    return 0;
  }
}

/**
 * Permission state machine helper. Returns one of:
 *   'unsupported' — Notification API missing (e.g. iOS Safari without PWA installed)
 *   'denied'      — user blocked notifications; only Settings change can revert
 *   'default'     — never asked; UI should prompt
 *   'granted'     — good to go
 */
export function getPermissionState() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/**
 * Friendly one-liner explaining the current permission state — drop into
 * Settings / Notification preferences pages.
 */
export function permissionMessage(state) {
  switch (state) {
    case 'unsupported':
      return 'This browser doesn\'t support push notifications. On iPhone, install Tirth ERP to your Home Screen first.';
    case 'denied':
      return 'Notifications were blocked. Enable them in your browser site settings to receive system alerts.';
    case 'default':
      return 'Click "Enable system notifications" so Tirth ERP can alert you about urgent events.';
    case 'granted':
      return 'System notifications are enabled.';
    default:
      return '';
  }
}
